import React, { useState } from "react";
import styled from "styled-components";
import SEO from "../components/SEO";
import { LocalBusinessSchema } from "../data/LocalBusinessSchema";
import { trackEvent } from "../analytics";

const ContactPageContainer = styled.div`
  flex: 1; /* Make container fill available vertical space */
  display: flex;
  flex-direction: column;
  justify-content: center; /* Center content vertically */
  padding: 20px;
  box-sizing: border-box;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 48px;
  color: #13519a;
  margin-bottom: 20px;
`;

const ContactWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  max-width: 1500px;
  width: 100%;
  margin: 0 auto;
`;

const InfoColumn = styled.div`
  flex: 1;
  min-width: 300px;

  h2 {
    font-size: 28px;
    color: #13519a;
    margin-bottom: 20px;
  }

  h4 {
    font-size: 20px;
    color: #2978aaff;
    margin-bottom: -30px;
  }

  p,
  .coord {
    font-size: 18px;
    line-height: 1.6;
    color: #d8d7dd; /* Softer light grey for text */
  }

  /* Bloc de coordonnées : div (un <h4> ne peut pas être dans un <p>), mêmes marges qu'un <p> */
  .coord {
    margin: 1em 0;
  }

  a {
    color: #ef6500; /* Consistent link color */
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const CoordHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 30px;
  margin-bottom: 20px;

  h2 {
    margin-bottom: 0;
  }
`;

const QRCode = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 8px;
  margin-left: 30px;
`;

const FormColumn = styled.div`
  flex: 2;
  min-width: 300px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  padding: 15px;
  font-size: 16px;
  border: 1px solid #d8d7dd;
  border-radius: 5px;
  font-family: "Roboto", sans-serif;
`;

const Textarea = styled.textarea`
  padding: 15px;
  font-size: 16px;
  border: 1px solid #d8d7dd;
  border-radius: 5px;
  font-family: "Roboto", sans-serif;
  min-height: 150px;
  resize: vertical;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  color: #d8d7dd; /* Softer light grey for text */

  a {
    color: #ef6500; /* Consistent link color */
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const CheckboxInput = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const SubmitButton = styled.button`
  width: 250px; /* Largeur du bouton */
  margin: 0 auto; /* Centrage horizontal */
  padding: 15px 35px; /* Copied from ActionButton */
  font-size: 20px; /* Copied from ActionButton */
  font-weight: 400; /* Copied from ActionButton */
  color: white; /* Copied from ActionButton */
  // background: linear-gradient(to right, #13519a, #2e73be, #00c3ff);
  background: linear-gradient(to right, #13519a, #5eb7ef);
  border: none; /* Copied from ActionButton */
  border-radius: 5px; /* Copied from ActionButton */
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease; /* Copied from ActionButton */
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2); /* Copied from ActionButton */

  &:hover {
    transform: translateY(-3px); /* Copied from ActionButton */
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3); /* Copied from ActionButton */
  }

  &:disabled {
    background: #d8d7dd;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

const TextareaWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Compteur = styled.span`
  position: absolute;
  right: 10px;
  bottom: 7px;
  font-size: 12px;
  color: #d8d7dd;
  pointer-events: none;
`;

const RequiredStar = styled.span`
  color: #e53935;
  margin-left: 4px;
  font-size: 18px;
  font-weight: bold;
  vertical-align: middle;
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
`;

const RequiredPopup = styled.span`
  color: #e53935;
  font-size: 12px;
  margin-top: 2px;
  display: block;
`;

const forbiddenChars = /[#{}()]/g;

function removeForbidden(str) {
  return str.replace(forbiddenChars, "");
}

const Contact = () => {
  const [consentChecked, setConsentChecked] = useState(false);
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [missingField, setMissingField] = useState(null);
  const maxLength = 500;

  // Validation email : accepte tous les formats standards (partie@domaine.extension)
  // [^\s@]+ = au moins 1 caractère qui n'est ni espace ni @
  // La regex accepte toutes les extensions (.fr, .com, .io, .eu, .tech, etc.)
  const validateEmail = (emailVal) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailVal.trim().toLowerCase());

  // Validation téléphone : exige au moins 10 chiffres (format français standard)
  // Autorise les espaces et tirets pour la lisibilité (06 12 34 56 78 ou 06-12-34-56-78)
  const validatePhone = (phoneVal) => {
    const digitsOnly = phoneVal.replace(/[^0-9]/g, "");
    return digitsOnly.length >= 10 && digitsOnly.length <= 15;
  };

  const handleNameChange = (e) => {
    setName(removeForbidden(e.target.value));
  };

  const handleEmailChange = (e) => {
    setEmail(removeForbidden(e.target.value));
  };

  const handleMessageChange = (e) => {
    setMessage(removeForbidden(e.target.value));
  };

  const handlePhoneChange = (e) => {
    const cleanValue = e.target.value.replace(/[^0-9\s-]/g, "");
    setPhone(cleanValue);
  };

  const InfoBubble = styled.span`
    display: inline-block;
    background: #ffe6e6;
    color: #e53935;
    font-size: 12px;
    border-radius: 6px;
    padding: 2px 8px;
    margin-left: 7px;
    position: relative;
    top: -2px;
    white-space: nowrap;
    box-shadow: 0 2px 6px rgba(229, 57, 53, 0.12);
  `;

  const nameIsInvalid = consentChecked && name.trim().length < 3;
  const emailInvalid =
    consentChecked && email.length > 0 && !validateEmail(email);
  const phoneInvalid =
    consentChecked &&
    phone.length > 0 &&
    (!validatePhone(phone) || phone.replace(/[^0-9]/g, "").length < 10);
  const messageInvalid =
    consentChecked && message.trim().length > 0 && message.trim().length < 20;
  const messageRequired = consentChecked && message.trim().length === 0;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMissingField(null);

    if (!consentChecked) {
      alert(
        "Veuillez accepter la politique de confidentialité pour envoyer le message."
      );
      return;
    }

    if (!name) {
      setMissingField("name");
      return;
    }
    if (!validateEmail(email)) {
      setMissingField("email");
      return;
    }
    if (!validatePhone(phone)) {
      setMissingField("phone");
      return;
    }
    if (!message) {
      setMissingField("message");
      return;
    }

    try {
      const response = await fetch('https://kwv9qw8t30.execute-api.eu-west-3.amazonaws.com/prod/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, phone, message })
      });

      const data = await response.json();

      if (data.success) {
        // Conversion principale du site : une demande de contact envoyée
        trackEvent("generate_lead", { form: "contact" });
        alert("Votre message a bien été envoyé !");
        // Réinitialiser le formulaire
        setName('');
        setEmail('');
        setPhone('');
        setMessage('');
        setConsentChecked(false);
      } else {
        alert("Erreur : " + (data.message || "Envoi échoué"));
      }
    } catch (error) {
      alert("Erreur de connexion au serveur");
    }
  };

  // Le formulaire est valide si tous les champs obligatoires sont remplis correctement
  const isFormValid =
    consentChecked &&
    name.trim().length >= 3 &&       // Nom : minimum 3 caractères
    validateEmail(email) &&          // Email : format valide
    validatePhone(phone) &&          // Téléphone : 10-15 chiffres
    message.trim().length >= 20;     // Message : minimum 20 caractères

  return (
    <ContactPageContainer id="ContactPageContainer">
      <SEO
        title="Contact | ImpactIsol - Demandez votre devis calorifugeage"
        description="Contactez ImpactIsol pour vos projets de calorifugeage et isolation thermique. Devis gratuit. Basé à Noyarey (38), intervention en Auvergne-Rhône-Alpes."
        jsonLd={LocalBusinessSchema}
      />
      <PageTitle>Nous Contacter</PageTitle>
      <ContactWrapper>
        <InfoColumn>
          <CoordHeader>
            <h2>Nos Coordonnées</h2>
            <QRCode src="/images/ImpactIsol_PremiumQR.png" alt="QR Code ImpactIsol" />
          </CoordHeader>
          <div className="coord">
            <h4>Adresse :</h4>
            <br />
            500 Route du Ruisset
            <br />
            38360 NOYAREY, France
          </div>
          <div className="coord">
            <h4>Téléphone :</h4>
            <br />
            <a href="tel:+33625837471" onClick={() => trackEvent("clic_telephone")}>+33 625 837 471</a>
          </div>
          <div className="coord">
            <h4>Email :</h4>
            <br />
            <a href="mailto:contact@impactisol.fr" onClick={() => trackEvent("clic_email")}>contact@impactisol.fr</a>
          </div>
        </InfoColumn>
        <FormColumn>
          <h2>Envoyez-nous un message</h2>
          <Form onSubmit={handleSubmit} autoComplete="off">
            <label htmlFor="name">
              Votre nom
              {nameIsInvalid && (
                <>
                  <RequiredStar visible={true}>*</RequiredStar>
                  <InfoBubble>
                    {name.length === 0
                      ? "Ce champ est requis"
                      : "Minimum 3 caractères"}
                  </InfoBubble>
                </>
              )}
            </label>
            <Input
              type="text"
              name="name"
              id="name"
              placeholder="Votre nom"
              minLength={3}
              maxLength={50}
              required
              autoComplete="off"
              value={name}
              onChange={handleNameChange}
            />
            {missingField === "name" && (
              <RequiredPopup>champ obligatoire</RequiredPopup>
            )}

            <label htmlFor="email">
              Votre email
              {consentChecked && !email && (
                <>
                  <RequiredStar visible={true}>*</RequiredStar>
                  <InfoBubble>Ce champ est requis</InfoBubble>
                </>
              )}
              {emailInvalid && (
                <>
                  <RequiredStar visible={true}>*</RequiredStar>
                  <InfoBubble>
                    Format attendu : nom@domaine.fr ou nom@domaine.com
                  </InfoBubble>
                </>
              )}
            </label>
            <Input
              type="email"
              name="email"
              id="email"
              placeholder="Votre email (ex: nom@domaine.fr/com)"
              required
              autoComplete="off"
              value={email}
              onChange={handleEmailChange}
            />
            {missingField === "email" && (
              <RequiredPopup>champ obligatoire</RequiredPopup>
            )}

            <label htmlFor="phone">
              Numéro de téléphone
              {consentChecked && !phone && (
                <>
                  <RequiredStar visible={true}>*</RequiredStar>
                  <InfoBubble>Ce champ est requis</InfoBubble>
                </>
              )}
              {phoneInvalid && (
                <>
                  <RequiredStar visible={true}>*</RequiredStar>
                  <InfoBubble>
                    Format attendu : 10 chiffres minimum
                    <br />
                    (ex : 0612345678)
                  </InfoBubble>
                </>
              )}
            </label>
            <Input
              type="tel"
              name="phone"
              id="phone"
              placeholder="Numéro de téléphone (ex : 0612345678)"
              pattern="^[0-9\s\-]+$"
              inputMode="numeric"
              autoComplete="off"
              maxLength={12}
              required
              value={phone}
              onChange={handlePhoneChange}
            />
            {missingField === "phone" && (
              <RequiredPopup>champ obligatoire</RequiredPopup>
            )}

            <label htmlFor="message">
              Message
              {messageRequired && (
                <>
                  <RequiredStar visible={true}>*</RequiredStar>
                  <InfoBubble>Ce champ est requis</InfoBubble>
                </>
              )}
              {messageInvalid && (
                <>
                  <RequiredStar visible={true}>*</RequiredStar>
                  <InfoBubble>20 caractères minimum</InfoBubble>
                </>
              )}
            </label>
            <TextareaWrapper>
              <Textarea
                name="message"
                id="message"
                placeholder="Décrivez votre besoin..."
                maxLength={maxLength}
                required
                autoComplete="off"
                value={message}
                onChange={handleMessageChange}
                style={{ paddingBottom: "25px" }}
              ></Textarea>
              <Compteur>
                {message.length} / {maxLength} caractères
              </Compteur>
            </TextareaWrapper>
            {missingField === "message" && (
              <RequiredPopup>champ obligatoire</RequiredPopup>
            )}

            <CheckboxContainer>
              <CheckboxInput
                type="checkbox"
                id="gdpr-consent"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                required
              />
              <label htmlFor="gdpr-consent">
                J'accepte que mes données soient utilisées pour répondre à ma
                demande, conformément à la{" "}
                <a
                  href="/politique-confidentialite"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Politique de Confidentialité
                </a>
                .
              </label>
            </CheckboxContainer>
            <SubmitButton type="submit" disabled={!isFormValid}>
              Envoyer
            </SubmitButton>
          </Form>
        </FormColumn>
      </ContactWrapper>
    </ContactPageContainer>
  );
};

export default Contact;
